import { useState } from "react";
import {
  AppBox,
  AppModal,
  AppPaper,
  AppSkeletonGlobalCardList,
  AppText,
  AppTitle,
} from "../../../../../commons/components";
import IconBin from "../../../../../commons/components/icons/bin";
import IconEdit from "../../../../../commons/components/icons/edit";
import { useAppDispatch, useAppSelector } from "../../../../../hooks/app";
import { useSnackbar } from "../../../../../redux/snackbarProvider";
import { seasonMatchCommentaryDelete } from "../../../../../redux/slices/football/admin/season/match/commentary/manage-season-match-commentary-delete-slice";
import { AxiosError } from "axios";
import { IServerErrorResponse } from "../../../../../commons/components/interface";
import MatchCommentaryDelete from "./MatchCommentaryDelete";
import { StatusEnum } from "../../../../../commons/enums/status-enum";
import MatchCommentaryDeleteInprogress from "./MatchCommentaryDeleteInprogress";
import { fetchSeasonMatchCommentaryList } from "../../../../../redux/slices/football/admin/season/match/commentary/manage-season-match-commentary-list-slice";
import MatchCommentaryEdit from "./MatchCommentaryEdit";
import { MatchEventTypeEnum } from "../../../../../commons/enums/match-event-type-enum";
import { fetchSeasonMatchDetail } from "../../../../../redux/slices/football/admin/season/match/manage-season-match-detail-slice";
import { MatchCommentaryMetaDataTypeEnum } from "../../../../../commons/enums/match-commentary-metadata-type-enum";
import {
  IFootballCommentary,
  IMatchCommentaryMetadata,
} from "../../../../../commons/models/football/interface/i-football-commentary";
import MatchManageCommentaryCard from "./MatchManageCommentaryCard";
import IconChat from "../../../../../commons/components/icons/chat";
import ability from "../../../../../casl/Can";
import { RolePolicyActionEnum } from "../../../../../commons/enums/role-policy-action.enum";
import { ManageFootballTeam } from "../../../../../commons/models/football/admin/manage-football-team";
import { useParams } from "react-router";

const MatchCommentaryList = () => {
  const dispatch = useAppDispatch();
  const addSnackbar = useSnackbar();
  const { teamId } = useParams();
  const { seasonMatchDetail } = useAppSelector((state) => state.footballManageSeasonMatchDetail);
  const { seasonMatchCommentaryList, seasonMatchCommentaryListStatus } = useAppSelector(
    (state) => state.footballManageSeasonMatchCommentaryList
  );
  const { seasonMatchCommentaryDeleteStatus } = useAppSelector(
    (state) => state.footballManageSeasonMatchCommentaryDelete
  );
  const { seasonMatchCommentaryEditResponseStatus } = useAppSelector(
    (state) => state.footballManageSeasonMatchCommentaryEdit
  );
  const [isEditMatchCommentary, setEditMatchCommentary] = useState(false);
  const [isDeleteMatchCommentary, setDeleteMatchCommentary] = useState(false);
  const [selectedCommentary, setSelectedCommentary] = useState<IFootballCommentary | null>(null);

  const handleDeleteConfirm = (e: Event) => {
    e.preventDefault();
    if (seasonMatchDetail && selectedCommentary) {
      setDeleteMatchCommentary(false);
      dispatch(
        seasonMatchCommentaryDelete({
          seasonId: seasonMatchDetail.season.id,
          matchId: seasonMatchDetail.id,
          commentaryId: selectedCommentary.id,
          urlParams: {
            hostTeamId: Number(teamId),
          },
        })
      )
        .unwrap()
        .then(() => {
          addSnackbar({
            key: "commentary-delete",
            text: "Commentary Deleted Successfully",
            variant: "success",
          });
          if (
            selectedCommentary.eventType === MatchEventTypeEnum.Goal ||
            selectedCommentary.eventType === MatchEventTypeEnum.OwnGoal ||
            (selectedCommentary.eventType === MatchEventTypeEnum.Penalty &&
              (selectedCommentary.metadata as IMatchCommentaryMetadata)["type"] ===
                MatchCommentaryMetaDataTypeEnum.Scored)
          ) {
            dispatch(
              fetchSeasonMatchDetail({
                matchId: seasonMatchDetail.id,
                seasonId: seasonMatchDetail.season.id,
                urlParams: {
                  hostTeamId: Number(teamId),
                },
              })
            );
          } else {
            dispatch(
              fetchSeasonMatchCommentaryList({
                matchId: seasonMatchDetail.id,
                seasonId: seasonMatchDetail.season.id,
                urlParams: {
                  hostTeamId: Number(teamId),
                },
              })
            );
          }
        })
        .catch((error: AxiosError<IServerErrorResponse>) => {
          addSnackbar({
            key: "error",
            text: error?.response?.data.message,
            variant: "danger",
          });
        });
    }
  };

  const handleDeleteClick = (commentary: IFootballCommentary) => {
    setSelectedCommentary(commentary);
    setDeleteMatchCommentary(true);
  };

  const handleEditClick = (commentary: IFootballCommentary) => {
    setSelectedCommentary(commentary);
    setEditMatchCommentary(true);
  };

  return (
    <>
      {seasonMatchCommentaryListStatus === StatusEnum.Loading ? (
        <AppSkeletonGlobalCardList numberOfItems={5} withText />
      ) : (
        <>
          {seasonMatchCommentaryList.length > 0 ? (
            <>
              <AppTitle as="h5">Match Commentary</AppTitle>
              <AppBox flexDirection="column" gap="xs" pl="xs">
                {seasonMatchCommentaryList.mapArray((commentary, index) => (
                  <MatchManageCommentaryCard
                    key={index}
                    match={seasonMatchDetail}
                    commentary={commentary}
                    onEdit={(commentary) => {
                      handleEditClick(commentary);
                    }}
                    onDelete={(commentary) => {
                      handleDeleteClick(commentary);
                    }}
                  />
                ))}
              </AppBox>
            </>
          ) : (
            <>
              {ability.cannot(RolePolicyActionEnum.UPDATE, seasonMatchDetail) &&
                ability.cannot(RolePolicyActionEnum.UPDATE, new ManageFootballTeam({ id: Number(teamId) })) && (
                  <AppBox flexDirection="column" gap="xs">
                    <AppTitle as="h5">Match Commentary</AppTitle>
                    <AppPaper shadow="xs">
                      <AppBox flexDirection="column" alignItems="center" gap="sm" justifyContent="center" p="lg">
                        <AppBox alignItems="center" flexDirection="column" justifyContent="center" gap="2xs">
                          <IconChat width={56} height={56} color="rgba(var(--border-200))" />
                          <AppTitle as="h6">No Info Available</AppTitle>
                          <AppText textAlign="center">
                            Updates for this match are not available yet. Please check back closer to kickoff for
                            real-time commentary.
                          </AppText>
                        </AppBox>
                      </AppBox>
                    </AppPaper>
                  </AppBox>
                )}
            </>
          )}
        </>
      )}
      <AppModal
        opened={isDeleteMatchCommentary}
        onClose={(e) => {
          setDeleteMatchCommentary(false);
        }}
        title="Want To Delete This Commentary?"
        titleIcon={<IconBin />}
        iconColor="danger"
        confirmButtonLabel="Delete"
        onSave={(e) => {
          handleDeleteConfirm(e as Event);
        }}
      >
        <MatchCommentaryDelete />
      </AppModal>

      <AppModal
        opened={seasonMatchCommentaryDeleteStatus === StatusEnum.Loading}
        title="Deleting Commentary"
        titleIcon={<IconBin />}
        iconColor="danger"
        withoutCancelButton
        withoutButtons
        withoutCloseButton
      >
        <MatchCommentaryDeleteInprogress />
      </AppModal>
      {selectedCommentary && (
        <AppModal
          opened={isEditMatchCommentary}
          onClose={(e) => {
            setEditMatchCommentary(false);
          }}
          title="Edit Commentary"
          titleIcon={<IconEdit />}
          withLoadingButton={seasonMatchCommentaryEditResponseStatus === StatusEnum.Loading}
          withoutButtons
        >
          <MatchCommentaryEdit
            match={seasonMatchDetail}
            commentaryData={selectedCommentary}
            onEdit={() => setEditMatchCommentary(false)}
          />
        </AppModal>
      )}
    </>
  );
};

export default MatchCommentaryList;
